import { useState, useEffect } from 'react';
import { EnvVar } from '@vault/shared';
import { apiClient } from '../services/api';
import toast from 'react-hot-toast';

export function useEnvVars(projectId: number | null) {
  const [envVars, setEnvVars] = useState<EnvVar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEnvVars = async () => {
    if (!projectId) {
      setEnvVars([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getProjectEnvVars(projectId);
      setEnvVars(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch environment variables';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const bulkUpsertEnvVars = async (envContent: string) => {
    if (!projectId) {
      throw new Error('No project selected');
    }

    try {
      setLoading(true);
      const data = await apiClient.bulkUpsertEnvVars({
        projectId,
        envVars: envContent,
      });
      setEnvVars(data);
      toast.success('Environment variables updated successfully');
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update environment variables';
      toast.error(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteEnvVar = async (id: number) => {
    try {
      await apiClient.deleteEnvVar(id);
      setEnvVars(prev => prev.filter(env => env.id !== id));
      toast.success('Environment variable deleted successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete environment variable';
      toast.error(message);
      throw err;
    }
  };

  useEffect(() => {
    fetchEnvVars();
  }, [projectId]);

  return {
    envVars,
    loading,
    error,
    bulkUpsertEnvVars,
    deleteEnvVar,
    refetch: fetchEnvVars,
  };
}